import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { hasWarehousePowers, isWarehouseView } from "@/utils/permissions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PendingOrdersBadge from "@/components/PendingOrdersBadge";
import {
  Menu,
  Plus,
  Search,
  Clock,
  Inbox,
  Archive,
  ChevronRight,
  Trash2,
  Route,
  Send,
} from "lucide-react";

// Nuovo state machine: DA_CLIENTE -> DA_TRASFERIRE -> RICEVUTO -> FATTURATO
const STATUS_CONFIG = {
  da_cliente: { label: "Da Cliente", icon: Inbox, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  da_trasferire: { label: "Da Trasferire", icon: Clock, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/30" },
  ricevuto: { label: "Ricevuto", icon: Inbox, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  fatturato: { label: "Fatturato", icon: Archive, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  // Legacy: rendering fallback per vecchi status_history (read-only)
  in_preparazione: { label: "Ricevuto", icon: Inbox, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  fatturazione: { label: "Ricevuto", icon: Inbox, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  chiuso: { label: "Fatturato", icon: Archive, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
};

// Etichette friendly per i clienti: nascondono i dettagli interni di workflow
const CLIENT_STATUS_CONFIG = {
  da_cliente: { label: "Inviato", icon: Send, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  da_trasferire: { label: "In elaborazione", icon: Clock, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/30" },
  ricevuto: { label: "Confermato", icon: Inbox, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  in_preparazione: { label: "Confermato", icon: Inbox, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  fatturazione: { label: "Confermato", icon: Inbox, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  fatturato: { label: "Completato", icon: Archive, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  chiuso: { label: "Completato", icon: Archive, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
};

const BASE_TABS = [
  { key: "attivi", label: "Attivi" },
  { key: "da_cliente", label: "Da Clienti", roles: ["admin", "agent"] },
  { key: "da_trasferire", label: "Da Trasferire", roles: ["agent", "admin"] },
  // Stati interni: il cliente non deve vederli (sono workflow admin/agente)
  { key: "ricevuto", label: "Ricevuti", roles: ["admin", "agent", "magazziniere"] },
  { key: "giri", label: "Giri", roles: ["admin", "agent", "magazziniere"] },
  { key: "fatturato", label: "Archivio" },
];

function StatusBadge({ status, forClient = false }) {
  const map = forClient ? CLIENT_STATUS_CONFIG : STATUS_CONFIG;
  const cfg = map[status] || map.ricevuto || STATUS_CONFIG.ricevuto;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

const ARCHIVE_PAGE_SIZE = 30;

export default function OrdersPage() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");
  const [activeTab, setActiveTab] = useState("attivi");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [roundDialogOpen, setRoundDialogOpen] = useState(false);
  const [newRoundName, setNewRoundName] = useState("");
  // Archive (paginated) state — caricato separatamente da `orders` per non
  // appesantire l'apertura della pagina su mobile.
  const [archivedItems, setArchivedItems] = useState([]);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedHasMore, setArchivedHasMore] = useState(false);
  const [archivedDebouncedSearch, setArchivedDebouncedSearch] = useState("");
  const [existingRounds, setExistingRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(false);

  const isWarehouse = isWarehouseView(user);
  const isClient = user?.role === "client";
  const canCreate = user?.role === "admin" || user?.role === "agent" || user?.role === "magazziniere";
  const canBulkRound = hasWarehousePowers(user);
  const canTransfer = user?.role === "admin" || user?.role === "agent";
  const basePath = (() => {
    if (isWarehouse) return "/warehouse";
    if (user?.role === "admin") return "/admin";
    if (isClient) return "/client";
    return "/agent";
  })();

  const TABS = useMemo(() => {
    return BASE_TABS.filter((t) => !t.roles || t.roles.includes(user?.role));
  }, [user?.role]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await authAxios.get("/orders");
      setOrders(res.data);
    } catch {
      toast.error("Errore nel caricamento ordini");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  // Carica la prima pagina di ordini archiviati (in lazy: solo metadati count
  // al primo render così la badge "Archivio (N)" è subito popolata, mentre il
  // dettaglio della lista viene caricato all'apertura del tab).
  const fetchArchivedCount = useCallback(async () => {
    try {
      const res = await authAxios.get("/orders/archived", { params: { limit: 1, skip: 0 } });
      setArchivedTotal(res.data?.total || 0);
    } catch (err) {
      // best-effort: se fallisce, il count rimane 0 ma il tab funziona.
      // Loggato a livello debug per non rumoreggiare in console.
      console.debug("[OrdersPage] Conteggio archivio non disponibile:", err?.message);
    }
  }, [authAxios]);

  const fetchArchivedPage = useCallback(
    async ({ reset = false } = {}) => {
      setArchivedLoading(true);
      try {
        const skip = reset ? 0 : archivedItems.length;
        const res = await authAxios.get("/orders/archived", {
          params: {
            limit: ARCHIVE_PAGE_SIZE,
            skip,
            search: archivedDebouncedSearch || undefined,
          },
        });
        const data = res.data || {};
        setArchivedTotal(data.total || 0);
        setArchivedHasMore(!!data.has_more);
        setArchivedItems((prev) => (reset ? data.items || [] : [...prev, ...(data.items || [])]));
      } catch {
        toast.error("Errore nel caricamento archivio");
      } finally {
        setArchivedLoading(false);
      }
    },
    [authAxios, archivedItems.length, archivedDebouncedSearch]
  );

  useEffect(() => {
    fetchOrders();
    fetchArchivedCount();
  }, [fetchOrders, fetchArchivedCount]);

  // Debounce della ricerca quando siamo sul tab Archivio (server-side search).
  useEffect(() => {
    if (activeTab !== "fatturato") return undefined;
    const t = setTimeout(() => setArchivedDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search, activeTab]);

  // Carica/ricarica la pagina archiviati quando si apre il tab o cambia search.
  useEffect(() => {
    if (activeTab !== "fatturato") return;
    fetchArchivedPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, archivedDebouncedSearch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const handleDelete = async (orderId) => {
    if (!window.confirm("Eliminare questo ordine?")) return;
    try {
      await authAxios.delete(`/orders/${orderId}`);
      toast.success("Ordine eliminato");
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      setArchivedItems((prev) => prev.filter((o) => o.id !== orderId));
      setArchivedTotal((t) => Math.max(0, t - 1));
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const filtered = useMemo(() => {
    // Archivio: mostra sia gli ordini fatturati recenti (ancora in `orders`,
    // non ancora archiviati su Dropbox) sia gli archiviati paginati.
    if (activeTab === "fatturato") {
      const recentFatturato = orders.filter(
        (o) => (o.status === "fatturato" || o.status === "chiuso") && !o.archived
      );
      // Server-side search: applichiamo lo stesso filtro client-side anche ai
      // fatturati recenti per coerenza con l'UX della casella di ricerca.
      const q = (archivedDebouncedSearch || "").toLowerCase();
      const matchSearch = (o) => {
        if (!q) return true;
        return (
          (o.numero || "").toLowerCase().includes(q) ||
          (o.client_name || "").toLowerCase().includes(q) ||
          (o.created_by_name || "").toLowerCase().includes(q)
        );
      };
      const recentFiltered = recentFatturato.filter(matchSearch);
      // I `archivedItems` arrivano già desc by created_at e già filtrati lato
      // server. Uniamo i recenti (anch'essi desc) sopra agli archiviati.
      const merged = [...recentFiltered, ...archivedItems];
      if (sortBy === "client_asc") {
        merged.sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
      } else if (sortBy === "created_asc") {
        merged.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      } else {
        merged.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      }
      return merged;
    }

    let list = orders;
    if (activeTab === "attivi") {
      // Tutti tranne fatturati
      list = list.filter((o) => o.status !== "fatturato" && o.status !== "chiuso");
    } else if (activeTab === "ricevuto") {
      // Solo ordini ricevuti NON ancora in un giro
      list = list.filter((o) => ["ricevuto", "in_preparazione", "fatturazione"].includes(o.status) && !o.delivery_round_id);
    } else if (activeTab === "giri") {
      // Ordini ricevuti già assegnati ad un giro consegne
      list = list.filter((o) => ["ricevuto", "in_preparazione", "fatturazione"].includes(o.status) && !!o.delivery_round_id);
    } else {
      list = list.filter((o) => o.status === activeTab);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          o.numero.toLowerCase().includes(q) ||
          o.client_name.toLowerCase().includes(q) ||
          (o.created_by_name || "").toLowerCase().includes(q)
      );
    }

    // Sort
    const cmpGiro = (a, b) => {
      // Parse "agente-giorno" (e.g. "3-1") into tuples for natural ordering
      const parse = (s) => {
        const parts = String(s || "").split("-");
        return [parseInt(parts[0], 10) || 999, parseInt(parts[1], 10) || 999];
      };
      const [aa, ag] = parse(a.giro_provenienza);
      const [ba, bg] = parse(b.giro_provenienza);
      if (aa !== ba) return aa - ba;
      if (ag !== bg) return ag - bg;
      return (a.created_at || "").localeCompare(b.created_at || "");
    };
    const cmpAgent = (a, b) => {
      const an = (a.created_by_name || "").toLowerCase();
      const bn = (b.created_by_name || "").toLowerCase();
      if (an !== bn) return an.localeCompare(bn);
      return (b.created_at || "").localeCompare(a.created_at || "");
    };
    const sorted = [...list];
    if (sortBy === "giro_asc") sorted.sort(cmpGiro);
    else if (sortBy === "agent_asc") sorted.sort(cmpAgent);
    else if (sortBy === "client_asc") {
      sorted.sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
    } else if (sortBy === "created_asc") {
      sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    } else {
      // default: created_desc (most recent first)
      sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return sorted;
  }, [orders, activeTab, search, sortBy, archivedItems, archivedDebouncedSearch]);

  const tabCounts = useMemo(() => {
    const ricevutiBase = orders.filter((o) => ["ricevuto", "in_preparazione", "fatturazione"].includes(o.status));
    const recentFatturato = orders.filter(
      (o) => (o.status === "fatturato" || o.status === "chiuso") && !o.archived
    ).length;
    const counts = {
      attivi: orders.filter((o) => o.status !== "fatturato" && o.status !== "chiuso").length,
      da_cliente: orders.filter((o) => o.status === "da_cliente").length,
      da_trasferire: orders.filter((o) => o.status === "da_trasferire").length,
      ricevuto: ricevutiBase.filter((o) => !o.delivery_round_id).length,
      giri: ricevutiBase.filter((o) => !!o.delivery_round_id).length,
      // Archivio = fatturati recenti (non ancora zippati) + archiviati su Dropbox
      fatturato: recentFatturato + archivedTotal,
    };
    return counts;
  }, [orders, archivedTotal]);

  // Azioni bulk disponibili a seconda del tab attivo
  const showRoundBtn = canBulkRound && activeTab === "ricevuto";
  const showTransferBtn = canTransfer && activeTab === "da_trasferire";
  const showCheckboxes = showRoundBtn || showTransferBtn;

  // Filter for round assignment: only RICEVUTO orders without an active delivery_round_id
  const eligibleForRound = useMemo(() => {
    if (!showRoundBtn) return new Set();
    return new Set(filtered.filter((o) => !o.delivery_round_id && o.status === "ricevuto").map((o) => o.id));
  }, [filtered, showRoundBtn]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pool = showRoundBtn ? filtered.filter((o) => eligibleForRound.has(o.id)) : filtered;
    if (selectedIds.size === pool.length && pool.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pool.map((o) => o.id)));
    }
  };

  const openRoundDialog = async () => {
    setRoundDialogOpen(true);
    setLoadingRounds(true);
    try {
      const res = await authAxios.get("/delivery-rounds");
      setExistingRounds(res.data.filter((r) => r.status === "active" && !r.bbb_exported_at));
    } catch {
      setExistingRounds([]);
    } finally {
      setLoadingRounds(false);
    }
  };

  const handleCreateDeliveryRound = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    setRoundDialogOpen(false);
    try {
      const res = await authAxios.post("/delivery-rounds", {
        order_ids: Array.from(selectedIds),
        name: newRoundName.trim() || null,
      });
      const label = res.data.name ? `${res.data.letter} ${res.data.name}` : res.data.letter;
      toast.success(`Giro ${label} creato con ${res.data.deliveries.length} consegne`);
      setSelectedIds(new Set());
      setNewRoundName("");
      navigate(`${basePath}/delivery-rounds/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione del giro");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleAddToExistingRound = async (roundId) => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    setRoundDialogOpen(false);
    try {
      const res = await authAxios.put(`/delivery-rounds/${roundId}/add-orders`, { order_ids: Array.from(selectedIds) });
      toast.success(`${selectedIds.size} ordini aggiunti al Giro ${res.data.letter}`);
      setSelectedIds(new Set());
      navigate(`${basePath}/delivery-rounds/${roundId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiunta al giro");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkTransfer = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await authAxios.put("/orders/bulk-transfer", { order_ids: Array.from(selectedIds) });
      toast.success(`${res.data.transferred} ordini trasferiti`);
      setSelectedIds(new Set());
      await fetchOrders();
    } catch {
      toast.error("Errore nel trasferimento");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="orders-page">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Ordini" />
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-white">Ordini</h1>
            <p className="text-zinc-400 text-xs sm:text-sm">Gestione ordini</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <PendingOrdersBadge />
            {canBulkRound && (
              <Link to={`${basePath}/delivery-rounds`}>
                <Button
                  size="default"
                  className="bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-500 hover:to-violet-500 text-white font-semibold shadow-lg shadow-fuchsia-500/20 ring-1 ring-fuchsia-400/30 px-4"
                  data-testid="view-rounds-btn"
                >
                  <Route className="w-5 h-5 sm:mr-2" />
                  <span className="hidden sm:inline text-sm">Giri Consegne</span>
                </Button>
              </Link>
            )}
            {canCreate && (
              <Link to={`${basePath}/orders/new`}>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="new-order-btn">
                  <Plus className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Nuovo Ordine</span>
                </Button>
              </Link>
            )}
            {isClient && (
              <Link to="/client/cart">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="client-new-order-btn">
                  <Plus className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Nuovo Ordine</span>
                  <span className="sm:hidden">Nuovo</span>
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                  : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? "bg-amber-500/20" : "bg-white/10"}`}>
                {tabCounts[tab.key] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search + Sort + Bulk Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="orders-search"
              placeholder="Cerca per numero, cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10 text-white placeholder:text-zinc-500"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              data-testid="orders-sort-select"
              className="w-full sm:w-[200px] bg-[#18181b] border-white/10 text-white shrink-0"
            >
              <SelectValue placeholder="Ordina per…" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10 text-white">
              <SelectItem value="created_desc" data-testid="sort-recent">Più recenti</SelectItem>
              <SelectItem value="created_asc" data-testid="sort-oldest">Più vecchi</SelectItem>
              <SelectItem value="giro_asc" data-testid="sort-giro">Giro di provenienza</SelectItem>
              <SelectItem value="agent_asc" data-testid="sort-agent">Agente</SelectItem>
              <SelectItem value="client_asc" data-testid="sort-client">Cliente</SelectItem>
            </SelectContent>
          </Select>
          {showCheckboxes && selectedIds.size > 0 && (
            <div className="flex gap-2 shrink-0">
              {showTransferBtn && (
                <Button
                  data-testid="bulk-transfer-btn"
                  size="sm"
                  className="bg-violet-500 hover:bg-violet-600 text-white font-medium whitespace-nowrap"
                  onClick={handleBulkTransfer}
                  disabled={bulkLoading}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {bulkLoading ? "..." : `Trasferisci (${selectedIds.size})`}
                </Button>
              )}
              {showRoundBtn && (
                <Button
                  data-testid="create-round-btn"
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-medium whitespace-nowrap"
                  onClick={openRoundDialog}
                  disabled={bulkLoading || selectedIds.size === 0}
                >
                  <Route className="w-3.5 h-3.5 mr-1.5" />
                  {bulkLoading ? "..." : `Giro (${selectedIds.size})`}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Select All */}
        {showCheckboxes && filtered.length > 0 && (
          <div className="flex items-center gap-3 mb-3 px-1">
            <Checkbox
              data-testid="select-all-checkbox"
              checked={(() => {
                const pool = showRoundBtn ? filtered.filter((o) => eligibleForRound.has(o.id)) : filtered;
                return pool.length > 0 && selectedIds.size === pool.length;
              })()}
              onCheckedChange={toggleSelectAll}
              className="border-white/20 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
            />
            <span className="text-sm text-zinc-400">
              {selectedIds.size > 0
                ? `${selectedIds.size} di ${showRoundBtn ? filtered.filter((o) => eligibleForRound.has(o.id)).length : filtered.length} selezionati`
                : "Seleziona tutti"}
            </span>
          </div>
        )}

        {/* Orders List */}
        {loading && activeTab !== "fatturato" ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-20 rounded-xl" />
            ))}
          </div>
        ) : activeTab === "fatturato" && archivedLoading && archivedItems.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-20 rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-10 text-center">
              <p className="text-zinc-400 text-sm">Nessun ordine trovato</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5">
            {filtered.map((order) => {
              const isFatturato = order.status === "fatturato" || order.status === "chiuso";
              const checkboxDisabled = showRoundBtn && !eligibleForRound.has(order.id);
              return (
                <Card
                  key={order.id}
                  data-testid={`order-card-${order.id}`}
                  className={`bg-zinc-800 border-zinc-700 shadow-md shadow-black/40 hover:border-zinc-600 hover:shadow-lg hover:shadow-black/50 hover:-translate-y-px transition-all cursor-pointer ${
                    selectedIds.has(order.id) ? "border-amber-500/40 bg-amber-500/5" : ""
                  } ${isFatturato ? "opacity-80" : ""}`}
                >
                  <CardContent className="p-2.5">
                    <div className="flex items-center gap-3">
                      {showCheckboxes && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            data-testid={`select-order-${order.id}`}
                            checked={selectedIds.has(order.id)}
                            disabled={checkboxDisabled}
                            onCheckedChange={() => !checkboxDisabled && toggleSelect(order.id)}
                            className="border-white/20 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 disabled:opacity-30"
                          />
                        </div>
                      )}
                      <div
                        className="flex-1 min-w-0 flex items-center justify-between gap-4"
                        onClick={() => navigate(`${basePath}/orders/${order.id}`)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <span className="text-white font-mono font-bold text-sm">{order.numero}</span>
                            <StatusBadge status={order.status} forClient={isClient} />
                            {!isClient && order.giro_provenienza && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 font-mono" data-testid={`order-giro-prov-${order.id}`}>
                                Giro {order.giro_provenienza}
                              </span>
                            )}
                            {!isClient && order.delivery_round_letter && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/30 font-mono">
                                Cons. {order.delivery_round_letter}
                              </span>
                            )}
                            {!isClient && order.post_transfer_edits?.length > 0 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium"
                                title="Modificato dopo il trasferimento"
                                data-testid={`post-transfer-edit-badge-${order.id}`}
                              >
                                ✏️ Modificato
                              </span>
                            )}
                            {!isClient && order.archived && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-300 border border-zinc-500/30 font-medium"
                                title="Archiviato su Dropbox — articoli on-demand"
                                data-testid={`archived-badge-${order.id}`}
                              >
                                📦 Archiviato
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-300 truncate">{order.client_name}</p>
                          {order.destinazione_nome && order.destinazione_nome !== order.client_name && (
                            <p className="text-xs text-cyan-400 truncate" data-testid={`order-destinazione-${order.id}`}>
                              <span className="text-zinc-600">→ </span>
                              {order.destinazione_nome}
                              {order.destinazione_indirizzo && (
                                <span className="text-zinc-500"> · {order.destinazione_indirizzo}</span>
                              )}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs text-zinc-500">
                              {new Date(order.created_at).toLocaleDateString("it-IT")}
                            </span>
                            {!isClient && order.created_by_name && (
                              <span className="text-xs text-zinc-500">da {order.created_by_name}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right shrink-0">
                            {!(isClient && user?.sconti_fissi) && (
                              <p className="text-white font-mono font-bold whitespace-nowrap">
                                {order.total?.toLocaleString("it-IT", { style: "currency", currency: "EUR" })}
                              </p>
                            )}
                            <p className="text-xs text-zinc-500 whitespace-nowrap">{order.items_count ?? order.items?.length ?? 0} articoli</p>
                          </div>
                          {user?.role === "admin" && order.status === "ricevuto" && !order.delivery_round_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`delete-order-${order.id}`}
                              className="text-zinc-500 hover:text-red-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(order.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          <ChevronRight className="w-5 h-5 text-zinc-600" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {/* Paginazione archivio: pulsante "Carica altri 30" */}
            {activeTab === "fatturato" && archivedHasMore && (
              <div className="pt-3 flex justify-center">
                <Button
                  data-testid="archive-load-more-btn"
                  onClick={() => fetchArchivedPage()}
                  disabled={archivedLoading}
                  variant="outline"
                  className="border-white/10 bg-[#18181b] text-zinc-200 hover:bg-white/5 hover:text-white"
                >
                  {archivedLoading
                    ? "Caricamento..."
                    : `⬇ Carica altri ${Math.min(ARCHIVE_PAGE_SIZE, archivedTotal - archivedItems.length)} ordini`}
                </Button>
              </div>
            )}
            {activeTab === "fatturato" && !archivedHasMore && archivedItems.length > 0 && (
              <p className="text-center text-xs text-zinc-600 pt-3" data-testid="archive-end-marker">
                Fine archivio — {archivedTotal} ordini totali
              </p>
            )}
          </div>
        )}
      </main>

      {/* Dialog: Scegli Giro Consegne */}
      <Dialog open={roundDialogOpen} onOpenChange={setRoundDialogOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Assegna a Giro Consegne</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Nome opzionale per il nuovo giro */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">
                Nome del giro <span className="text-zinc-600">(opzionale)</span>
              </label>
              <Input
                value={newRoundName}
                onChange={(e) => setNewRoundName(e.target.value)}
                placeholder="es. Costa Naso, Capo Milazzo..."
                maxLength={80}
                className="bg-[#09090b] border-white/10 text-white text-sm h-9"
                data-testid="new-round-name-input"
              />
            </div>

            {/* Crea nuovo giro */}
            <button
              data-testid="new-round-option"
              onClick={handleCreateDeliveryRound}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center shrink-0">
                <Plus className="w-5 h-5 text-orange-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {newRoundName.trim() ? `Crea giro "${newRoundName.trim()}"` : "Crea nuovo giro"}
                </p>
                <p className="text-zinc-500 text-xs">{selectedIds.size} ordini selezionati</p>
              </div>
            </button>

            {/* Giri esistenti (solo non ancora BBB-esportati) */}
            {loadingRounds ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500" />
              </div>
            ) : existingRounds.length > 0 ? (
              <>
                <p className="text-xs text-zinc-500 px-1">oppure aggiungi a un giro esistente:</p>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {existingRounds.map((r) => (
                    <button
                      key={r.id}
                      data-testid={`existing-round-${r.id}`}
                      onClick={() => handleAddToExistingRound(r.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:border-orange-500/30 hover:bg-white/5 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                        <span className="text-orange-400 font-mono font-bold text-lg">{r.letter}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">
                          Giro {r.letter}{r.name ? ` ${r.name}` : ""} del {r.date
                            ? new Date(r.date + "T00:00:00").toLocaleDateString("it-IT")
                            : new Date(r.created_at).toLocaleDateString("it-IT")}
                        </p>
                        <p className="text-zinc-500 text-xs">{r.deliveries?.length || 0} consegne</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { StatusBadge, STATUS_CONFIG, CLIENT_STATUS_CONFIG };
